import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCart, removeCartItem, updateCartItem, getImageUrl } from '../api';
import API from '../api';
import { KM_VARS, KM_BASE } from './km-styles';

const SIZE_UNITS = ['S', 'M', 'L', 'XL', 'XXL'];

const toSlug = (name) =>
    name?.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || '';

const getVariantLabel = (variant) => {
    if (!variant) return 'Default';
    const name = (variant.variantName || '').trim();
    const unit = (variant.unit || '').trim();
    if (SIZE_UNITS.includes(unit.toUpperCase())) return unit.toUpperCase();
    if (name.toUpperCase() === unit.toUpperCase()) return unit;
    if (name && unit) return `${name} ${unit}`;
    return name || unit || 'Default';
};

const getCustomerId = () => {
    const customer = JSON.parse(localStorage.getItem('customer'));
    return customer?.id || null;
};

// ─── Clear localStorage if a different user is now logged in ─────────────────
const clearIfUserChanged = (customerId) => {
    const storedOwnerId = localStorage.getItem('cartOwnerId');
    if (storedOwnerId && storedOwnerId !== String(customerId)) {
        localStorage.removeItem('cart');
        localStorage.removeItem('wishlist');
        localStorage.removeItem('wishlistOwnerId');
    }
    if (customerId) {
        localStorage.setItem('cartOwnerId', String(customerId));
    }
};

// ─── Normalize variantId (must match ShopSection logic) ───────────────────────
const normalizeVariantId = (variantId) => {
    if (variantId === null || variantId === undefined) return 'default';
    if (variantId === 'default') return 'default';
    return String(variantId);
};

const makeCartId = (productId, variantId) =>
    `${productId}-${normalizeVariantId(variantId)}`;

// ─── QtyControl ───────────────────────────────────────────────────────────────
const QtyControl = ({ quantity, stock, onChange }) => {
    const maxQty = parseInt(stock ?? 999);
    return (
        <div style={{ display: 'flex', alignItems: 'center', border: '1.5px solid #E5E7EB', borderRadius: 10, overflow: 'hidden', width: 'fit-content' }}>
            <button
                onClick={() => onChange(Math.max(1, quantity - 1))}
                disabled={quantity <= 1}
                style={{ width: 34, height: 34, border: 'none', background: 'transparent', fontSize: 18, color: '#C2602C', cursor: quantity <= 1 ? 'not-allowed' : 'pointer', opacity: quantity <= 1 ? 0.4 : 1 }}
            >−</button>
            <span style={{ minWidth: 36, textAlign: 'center', fontWeight: 700, fontSize: 14, color: '#1C0F06' }}>{quantity}</span>
            <button
                onClick={() => onChange(Math.min(maxQty, quantity + 1))}
                disabled={quantity >= maxQty}
                style={{ width: 34, height: 34, border: 'none', background: 'transparent', fontSize: 18, color: '#C2602C', cursor: quantity >= maxQty ? 'not-allowed' : 'pointer', opacity: quantity >= maxQty ? 0.4 : 1 }}
            >+</button>
        </div>
    );
};

const CartSection = () => {
    const [cartItems, setCartItems] = useState([]);
    const [loading,   setLoading]   = useState(true);
    const [error,     setError]     = useState(null);
    const customerId = getCustomerId();
    const navigate   = useNavigate();

    useEffect(() => {
        if (!customerId) { setLoading(false); return; }

        // ── Guard: clear stale data from a previous user ──────────────────────
        clearIfUserChanged(customerId);

        const loadCart = async () => {
            try {
                const res   = await getCart(customerId);
                const items = res.data || [];

                if (items.length > 0) {
                    // ── DB has items: fetch fresh stock for each variant ──────────────
                    const itemsWithStock = await Promise.all(
                        items.map(async (item) => {
                            try {
                                const vRes     = await API.get(`/variants/product/${item.productId}`);
                                const variants = vRes.data?.data || vRes.data || [];
                                let matched    = null;

                                if (item.variantId) {
                                    matched = variants.find(v => String(v.id) === String(item.variantId));
                                }

                                const stock = matched?.stock ?? variants[0]?.stock ?? 999;
                                return { ...item, stock: parseInt(stock) };
                            } catch {
                                return { ...item, stock: 999 };
                            }
                        })
                    );

                    // ── Sync only stock values into localStorage, don't overwrite items ──
                    const localCart = JSON.parse(localStorage.getItem('cart')) || [];
                    const updatedLocal = localCart.map(lc => {
                        const serverItem = itemsWithStock.find(
                            si =>
                                String(si.productId) === String(lc.id) &&
                                normalizeVariantId(si.variantId) === normalizeVariantId(lc.variantId)
                        );
                        if (serverItem) return { ...lc, stock: serverItem.stock };
                        return lc;
                    });
                    localStorage.setItem('cart', JSON.stringify(updatedLocal));
                    window.dispatchEvent(new Event('storage'));

                    setCartItems(itemsWithStock);

                } else {
                    // ── DB cart is empty — fall back to localStorage ──────────────────
                    const localCart = JSON.parse(localStorage.getItem('cart')) || [];

                    if (localCart.length > 0) {
                        // Re-push localStorage items into DB so they stay in sync
                        for (const lc of localCart) {
                            try {
                                await fetch(`${process.env.REACT_APP_API_URL}/cart/add`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({
                                        customerId,
                                        productId:   lc.id,
                                        variantId:   lc.variantId === 'default' ? null : lc.variantId,
                                        variantName: lc.variantName || null,
                                        name:        lc.name,
                                        img:         lc.img,
                                        price:       lc.price,
                                        quantity:    lc.quantity,
                                        weight:      lc.weight,
                                    }),
                                });
                            } catch (syncErr) {
                                console.error('Failed to sync item to DB:', lc.name, syncErr);
                            }
                        }

                        // Now fetch fresh from DB after re-sync
                        try {
                            const retryRes   = await getCart(customerId);
                            const retryItems = retryRes.data || [];

                            const itemsWithStock = await Promise.all(
                                retryItems.map(async (item) => {
                                    try {
                                        const vRes     = await API.get(`/variants/product/${item.productId}`);
                                        const variants = vRes.data?.data || vRes.data || [];
                                        let matched    = null;
                                        if (item.variantId) {
                                            matched = variants.find(v => String(v.id) === String(item.variantId));
                                        }
                                        const stock = matched?.stock ?? variants[0]?.stock ?? 999;
                                        return { ...item, stock: parseInt(stock) };
                                    } catch {
                                        return { ...item, stock: 999 };
                                    }
                                })
                            );

                            setCartItems(itemsWithStock);
                        } catch {
                            // DB retry failed — show localStorage items directly
                            setCartItems(
                                localCart.map(lc => ({
                                    id:        lc.cartId,
                                    productId: lc.id,
                                    variantId: lc.variantId === 'default' ? null : lc.variantId,
                                    name:      lc.name,
                                    img:       lc.img,
                                    price:     lc.price,
                                    quantity:  lc.quantity,
                                    weight:    lc.weight,
                                    stock:     lc.stock ?? 999,
                                }))
                            );
                        }
                    } else {
                        // Both DB and localStorage are empty — cart is truly empty
                        setCartItems([]);
                    }
                }

            } catch {
                // API completely failed — fall back to localStorage
                const localCart = JSON.parse(localStorage.getItem('cart')) || [];
                if (localCart.length > 0) {
                    setCartItems(
                        localCart.map(lc => ({
                            id:        lc.cartId,
                            productId: lc.id,
                            variantId: lc.variantId === 'default' ? null : lc.variantId,
                            name:      lc.name,
                            img:       lc.img,
                            price:     lc.price,
                            quantity:  lc.quantity,
                            weight:    lc.weight,
                            stock:     lc.stock ?? 999,
                        }))
                    );
                } else {
                    setError('Failed to load cart. Please try again.');
                }
            } finally {
                setLoading(false);
            }
        };

        loadCart();
    }, [customerId]);

    const handleProductClick = (item) => {
        const stockQty     = parseInt(item.stock ?? 999);
        const isOutOfStock = stockQty === 0;

        navigate(`/product/${toSlug(item.name)}`, {
            state: {
                product: {
                    id:           item.productId,
                    productName:  item.name,
                    name:         item.name,
                    price:        item.price,
                    img:          getImageUrl(item.img),
                    image:        item.img,
                    salesPrice:   item.price,
                    mrp:          item.mrp || item.price,
                    weight:       item.weight,
                    stock:        stockQty,
                    isOutOfStock: isOutOfStock,
                }
            }
        });
    };

    const handleRemove = async (id) => {
        try {
            const removedItem = cartItems.find(i => i.id === id);

            await removeCartItem(id);

            if (removedItem) {
                const localCart = JSON.parse(localStorage.getItem('cart')) || [];
                const removedCartId = makeCartId(
                    removedItem.productId,
                    removedItem.variantId
                );
                const filtered = localCart.filter(lc => lc.cartId !== removedCartId);
                localStorage.setItem('cart', JSON.stringify(filtered));
                window.dispatchEvent(new Event('storage'));
            }

            setCartItems(prev => prev.filter(item => item.id !== id));

        } catch (err) {
            console.error('Remove cart item error:', err);
            alert('Could not remove item. Please try again.');
        }
    };

    const handleQuantityChange = async (id, newQty) => {
        const item        = cartItems.find(i => i.id === id);
        const actualStock = parseInt(item?.stock ?? 999);

        if (actualStock === 0) return;

        const qty = Math.min(Math.max(1, newQty), actualStock);

        try {
            await updateCartItem(id, { quantity: qty });

            setCartItems(prev => prev.map(i => i.id === id ? { ...i, quantity: qty } : i));

            if (item) {
                const localCart = JSON.parse(localStorage.getItem('cart')) || [];
                const cartId    = makeCartId(item.productId, item.variantId);
                const idx       = localCart.findIndex(lc => lc.cartId === cartId);
                if (idx !== -1) {
                    localCart[idx].quantity = qty;
                    localStorage.setItem('cart', JSON.stringify(localCart));
                    window.dispatchEvent(new Event('storage'));
                }
            }
        } catch (err) {
            console.error('Update quantity error:', err);
            alert('Could not update quantity. Please try again.');
        }
    };

  const subtotal = cartItems
    .filter(item => parseInt(item.stock ?? 999) > 0)
    .reduce((sum, item) => sum + (item.price || 0) * item.quantity, 0);

const total = subtotal;

    const hasOutOfStockItems = cartItems.some(item => parseInt(item.stock ?? 999) === 0);

    if (loading) return (
        <section className="km-page">
            <style>{KM_VARS + KM_BASE}</style>
            <div className="km-full">
                <div className="km-loader"><div className="km-spinner" /><p>Loading your cart…</p></div>
            </div>
        </section>
    );

    if (!customerId) return (
        <section className="km-page">
            <style>{KM_VARS + KM_BASE}</style>
            <div className="km-full">
                <div className="km-empty" style={{ margin: '0 auto' }}>
                    <div className="km-empty-icon">🔒</div>
                    <h4>Please log in to view your cart</h4>
                    <p>Sign in to access your saved cart items.</p>
                    <button className="km-btn-primary" onClick={() => navigate('/account')}>
                        Go to Login <i className="ph ph-arrow-right" />
                    </button>
                </div>
            </div>
        </section>
    );

    return (
        <section className="km-page">
            <style>{KM_VARS + KM_BASE + CART_STYLES}</style>
            <div className="km-full">
                {error && <div className="km-err">{error}</div>}

                <div className="km-ph">
                    <h1><i className="ph ph-shopping-cart" /> Shopping Cart</h1>
                    <p>Dream it. Find it. Love it.</p>
                </div>

                {cartItems.length > 0 ? (
                    <div className="cart-layout">

                        {/* ── Cart Table ── */}
                        <div className="cart-main">
                            <div className="km-card">
                                <div className="km-card-hd">
                                    <span style={{ marginRight: 'auto', fontSize: 13, color: 'rgba(255,255,255,0.5)', fontWeight: 600 }}>
                                        <i className="ph ph-shopping-cart" /> Shopping Cart
                                    </span>
                                </div>

                                {hasOutOfStockItems && (
                                    <div style={{
                                        background: '#FEF2F2',
                                        border: '1px solid #FECACA',
                                        borderRadius: 10,
                                        padding: '10px 16px',
                                        margin: '12px 16px 0',
                                        fontSize: 13,
                                        color: '#B91C1C',
                                        fontWeight: 600,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 8,
                                    }}>
                                        <i className="ph ph-warning-circle" style={{ fontSize: 18 }} />
                                        Some products are out of stock. Please remove them before checkout.
                                    </div>
                                )}

                                <div className="km-table-wrap">
                                    <table className="km-table">
                                        <thead>
                                            <tr>
                                                <th style={{ width: 48 }}></th>
                                                <th>Product</th>
                                                <th>Variant</th>
                                                <th>Price</th>
                                                <th>Quantity</th>
                                                <th>Subtotal</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {cartItems.map(item => {
                                                const stockQty     = parseInt(item.stock ?? 999);
                                                const isOutOfStock = stockQty === 0;
                                                const isLowStock   = stockQty > 0 && stockQty <= 5;

                                                return (
                                                    <tr key={item.id} style={{ opacity: isOutOfStock ? 0.6 : 1 }}>
                                                        <td>
                                                            <button className="km-rm-btn" onClick={() => handleRemove(item.id)} title="Remove">
                                                                <i className="ph ph-x" />
                                                            </button>
                                                        </td>

                                                        {/* ── Product ── */}
                                                        <td>
                                                            <div className="km-prod-cell">
                                                                <div className="km-img-w" style={{ position: 'relative' }}>
                                                                    <img src={getImageUrl(item.img)} alt={item.name} />
                                                                    {isOutOfStock && (
                                                                        <div style={{
                                                                            position: 'absolute', inset: 0,
                                                                            background: 'rgba(185,28,28,0.15)',
                                                                            borderRadius: 8,
                                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                        }}>
                                                                            <span style={{
                                                                                background: '#B91C1C', color: '#fff',
                                                                                fontSize: 9, fontWeight: 800,
                                                                                padding: '2px 5px', borderRadius: 4,
                                                                            }}>OUT OF STOCK</span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                <div>
                                                                    <span
                                                                        className="km-pname"
                                                                        onClick={() => handleProductClick(item)}
                                                                        style={{ cursor: 'pointer' }}
                                                                    >
                                                                        {item.name}
                                                                    </span>
                                                                    {isOutOfStock && (
                                                                        <span style={{
                                                                            display: 'block', fontSize: 10, fontWeight: 700,
                                                                            color: '#B91C1C', background: '#FEF2F2',
                                                                            border: '1px solid #FECACA', borderRadius: 6,
                                                                            padding: '1px 6px', marginTop: 4, width: 'fit-content'
                                                                        }}>
                                                                            Out of Stock
                                                                        </span>
                                                                    )}
                                                                    {isLowStock && (
                                                                        <span style={{
                                                                            display: 'block', fontSize: 10, fontWeight: 700,
                                                                            color: '#C2410C', background: '#FFF7ED',
                                                                            border: '1px solid #FED7AA', borderRadius: 6,
                                                                            padding: '1px 6px', marginTop: 4, width: 'fit-content'
                                                                        }}>
                                                                            Only {stockQty} left!
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </td>

                                                        {/* ── Variant ── */}
                                                        <td>
                                                            {item.weight ? (
                                                                <span style={{
                                                                    display:      'inline-block',
                                                                    padding:      '3px 10px',
                                                                    background:   '#FFF4EC',
                                                                    border:       '1.5px solid #F57C00',
                                                                    borderRadius: 6,
                                                                    fontSize:     12,
                                                                    fontWeight:   700,
                                                                    color:        '#C2410C',
                                                                }}>
                                                                    {[...new Set(item.weight.trim().split(/\s+/))].join(' ')}
                                                                </span>
                                                            ) : (
                                                                <span style={{ color: '#9CA3AF', fontSize: 12 }}>—</span>
                                                            )}
                                                        </td>

                                                        {/* ── Price ── */}
                                                        <td>
                                                            <span className="km-price">₹{item.price}</span>
                                                        </td>

                                                        {/* ── Quantity ── */}
                                                        <td>
                                                            {isOutOfStock ? (
                                                                <span style={{ fontSize: 12, color: '#9CA3AF', fontStyle: 'italic' }}>—</span>
                                                            ) : (
                                                                <QtyControl
                                                                    quantity={item.quantity}
                                                                    stock={item.stock}
                                                                    onChange={(q) => handleQuantityChange(item.id, q)}
                                                                />
                                                            )}
                                                        </td>

                                                        {/* ── Subtotal ── */}
                                                        <td>
                                                            <span className="km-subtotal">
                                                                {isOutOfStock
                                                                    ? <span style={{ color: '#B91C1C', fontSize: 12, fontWeight: 700 }}>—</span>
                                                                    : `₹${(item.price * item.quantity).toFixed(2)}`
                                                                }
                                                            </span>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="km-card-ft">
                                    <button className="km-btn-outline" onClick={() => navigate('/shop')}>
                                        <i className="ph ph-arrow-left" /> Continue Shopping
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* ── Order Summary ── */}
                        <div className="cart-sidebar">
                            <div className="km-sum-card">
                                <div className="km-sum-hd">
                                    <div className="km-sum-title">Order Summary</div>
                                    <div className="km-swoosh" />
                                </div>
                                <div className="km-sum-body">
                                  
                                   
                                   
                                    <div className="km-total-row">
                                        <span className="km-total-lbl">Total</span>
                                        <span className="km-total-val">₹{total.toFixed(2)}</span>
                                    </div>

                                    <button
                                        className="km-checkout-btn"
                                        onClick={() => !hasOutOfStockItems && navigate('/checkout')}
                                        disabled={hasOutOfStockItems}
                                        style={{
                                            opacity: hasOutOfStockItems ? 0.5 : 1,
                                            cursor:  hasOutOfStockItems ? 'not-allowed' : 'pointer',
                                        }}
                                    >
                                        Proceed to Checkout <i className="ph ph-arrow-right" />
                                    </button>

                                    {hasOutOfStockItems && (
                                        <p style={{ fontSize: 11, color: '#B91C1C', textAlign: 'center', marginTop: 8, fontWeight: 600 }}>
                                            Please remove out-of-stock items
                                        </p>
                                    )}

                                    <div className="km-trust">
                                        <span><i className="ph ph-lock" /> Secure</span>
                                        <span><i className="ph ph-shield-check" /> Safe Pay</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="km-empty">
                        <div className="km-empty-icon"><i className="ph ph-shopping-cart" /></div>
                        <h4>Your cart is empty</h4>
                        <p>Looks like you haven't added anything yet!</p>
                        <button className="km-btn-primary" onClick={() => navigate('/shop')}>
                            Go to Shop <i className="ph ph-arrow-right" />
                        </button>
                    </div>
                )}
            </div>
        </section>
    );
};

const CART_STYLES = `
.cart-layout { 
  display: grid; 
  grid-template-columns: 1fr 340px; 
  gap: 24px; 
  align-items: start; 
}

@media(max-width:1024px){ 
  .cart-layout { 
    grid-template-columns: 1fr; 
  } 
}

@media (max-width: 768px) {

  .km-table thead {
    display: none;
  }

  .km-table, 
  .km-table tbody, 
  .km-table tr, 
  .km-table td {
    display: block;
    width: 100%;
  }

  .km-table tr {
    background: #fff;
    border: 1px solid #eee;
    border-radius: 12px;
    margin-bottom: 16px;
    padding: 12px;
    position: relative;
  }

  .km-table td {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    border: none;
    font-size: 14px;
  }

  .km-table td:nth-child(2) {
    justify-content: flex-start;
    border-bottom: 1px solid #f5f5f5;
    padding-bottom: 12px;
    margin-bottom: 8px;
  }

  .km-table td:nth-child(3)::before { content: "Variant";  font-weight: 600; color: #666; }
  .km-table td:nth-child(4)::before { content: "Price";    font-weight: 600; color: #666; }
  .km-table td:nth-child(5)::before { content: "Qty";      font-weight: 600; color: #666; }
  .km-table td:nth-child(6)::before { content: "Subtotal"; font-weight: 600; color: #666; }

  .km-table td:nth-child(1) {
    position: absolute;
    top: 10px;
    right: 10px;
    width: auto;
  }

  .km-prod-cell { gap: 12px; }
  .km-img-w     { width: 60px; height: 60px; }
  .km-pname     { font-size: 15px; }

  .km-table td:nth-child(5) { justify-content: space-between; }
  .cart-sidebar              { margin-top: 16px; }
}
`;

export default CartSection;